import React, { useEffect, useRef, useState, useCallback } from 'react';
import maplibregl from 'maplibre-gl';
import type {
  OSRMRoute,
  RouteWaypoint,
  HourlyWeatherSample,
  WeatherLayer,
  WeatherAlert,
  UnitsMode,
  TripEndpointRow,
} from '../types';
import { getMapLibreStyle } from '../utils/mapStyle';
import { pointAlongProgress } from '../utils/geo';
import { getWeatherCategory } from '../services/openMeteo';
import { thinTickerIndices } from '../utils/tickerPins';
import { severityColor } from '../services/weatherAlerts';
import { formatTickerMetric } from '../utils/tickerMetric';
import {
  getWeatherSvgString,
  getCarSvgString,
  getDropSvgString,
  getWindSvgString,
  getSunSvgString,
} from './icons/OnTheRouteIcons';

type Rect = {
  left: number;
  right: number;
  top: number;
  bottom: number;
};

type ManagedPill = {
  marker: maplibregl.Marker;
  el: HTMLDivElement;
  wp: RouteWaypoint;
  idx: number;
  isEndpoint: boolean;
};

type MapViewProps = {
  routes: OSRMRoute[];
  selectedRouteIndex: number;
  onSelectRouteIndex: (idx: number) => void;
  waypoints: RouteWaypoint[];
  waypointWeather: Map<string, HourlyWeatherSample>;
  routeAlerts: Map<string, WeatherAlert[]>;
  activeLayer: WeatherLayer;
  unitsMode: UnitsMode;
  departureTime: Date;
  selectedWaypointId: string | null;
  onSelectWaypointId: (id: string | null) => void;
  weatherLoading?: boolean;
  scrubProgress: number | null; // null if scrub panel closed
  isDark: boolean;
  precipTileUrl: string | null;
  isDesktop: boolean;
  endpoints?: TripEndpointRow[];
};

export const MapView: React.FC<MapViewProps> = ({
  routes,
  selectedRouteIndex,
  onSelectRouteIndex,
  waypoints,
  waypointWeather,
  routeAlerts,
  activeLayer,
  unitsMode,
  selectedWaypointId,
  onSelectWaypointId,
  weatherLoading = false,
  scrubProgress,
  isDark,
  precipTileUrl,
  isDesktop,
  endpoints = [],
}) => {
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<maplibregl.Map | null>(null);
  const markersRef = useRef<maplibregl.Marker[]>([]);
  const managedPillsRef = useRef<ManagedPill[]>([]);
  const updatePillCollisionsRef = useRef<() => void>(() => {});
  const endpointMarkersRef = useRef<maplibregl.Marker[]>([]);
  const scrubMarkerRef = useRef<maplibregl.Marker | null>(null);

  const [isMapLoaded, setIsMapLoaded] = useState(false);

  // Initialize MapLibre
  useEffect(() => {
    if (!mapContainerRef.current || mapRef.current) return;

    const map = new maplibregl.Map({
      container: mapContainerRef.current,
      style: getMapLibreStyle(isDark),
      center: [-98.5795, 39.8283], // Center of US
      zoom: 4,
      attributionControl: false,
    });

    const updateViewport = () => {
      if (!map) return;
      updatePillCollisionsRef.current();
    };

    map.on('load', () => {
      setIsMapLoaded(true);
      updateViewport();
    });

    let moveTimeout: ReturnType<typeof setTimeout> | null = null;
    const debouncedUpdateViewport = () => {
      if (moveTimeout) clearTimeout(moveTimeout);
      moveTimeout = setTimeout(() => {
        updateViewport();
      }, 100);
    };

    map.on('move', () => {
      updatePillCollisionsRef.current();
      debouncedUpdateViewport();
    });

    map.on('moveend', () => {
      if (moveTimeout) clearTimeout(moveTimeout);
      updateViewport();
    });

    map.on('zoom', () => {
      updatePillCollisionsRef.current();
    });

    map.on('zoomend', () => {
      if (moveTimeout) clearTimeout(moveTimeout);
      updateViewport();
    });

    map.on('resize', () => {
      updateViewport();
    });

    const resizeObserver = new ResizeObserver(() => {
      map.resize();
      updateViewport();
    });
    if (mapContainerRef.current) {
      resizeObserver.observe(mapContainerRef.current);
    }

    mapRef.current = map;

    return () => {
      if (moveTimeout) clearTimeout(moveTimeout);
      resizeObserver.disconnect();
      map.remove();
      mapRef.current = null;
      setIsMapLoaded(false);
    };
  }, []);

  // Update style when theme changes
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    map.setStyle(getMapLibreStyle(isDark));
  }, [isDark]);

  // Hourly precipitation forecast overlay (WeatherAPI tiles — not live radar)
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    const updatePrecip = () => {
      if (map.getLayer('precip-layer')) map.removeLayer('precip-layer');
      if (map.getSource('precip-forecast')) map.removeSource('precip-forecast');

      if (precipTileUrl) {
        map.addSource('precip-forecast', {
          type: 'raster',
          tiles: [precipTileUrl],
          tileSize: 256,
          minzoom: 3,
          maxzoom: 8,
        });
        const beforeLayer = map.getLayer('route-halo') ? 'route-halo' : undefined;
        map.addLayer(
          {
            id: 'precip-layer',
            type: 'raster',
            source: 'precip-forecast',
            paint: {
              'raster-resampling': 'linear',
              'raster-fade-duration': 300,
              'raster-opacity': [
                'interpolate',
                ['linear'],
                ['zoom'],
                3,
                0.20,
                5,
                0.40,
                7,
                0.60,
              ],
            },
          },
          beforeLayer,
        );
      }
    };

    if (map.isStyleLoaded()) {
      updatePrecip();
    } else {
      const handler = () => {
        if (map.isStyleLoaded()) {
          map.off('load', handler);
          map.off('styledata', handler);
          updatePrecip();
        }
      };
      map.on('load', handler);
      map.on('styledata', handler);
    }
  }, [precipTileUrl, isDark]);

  // Update Route Polyline & Fit Bounds
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    const activeRoute = routes[selectedRouteIndex];
    if (!activeRoute) {
      if (map.getSource('route-primary')) {
        (map.getSource('route-primary') as maplibregl.GeoJSONSource).setData({
          type: 'FeatureCollection',
          features: [],
        });
      }
      return;
    }

    const updateRoutes = () => {
      const C = isDark
        ? {
            route: '#3D95FF',
            routeCase: '#0B4FB0',
            halo: '#121417',
            alt: '#5E7FA8',
          }
        : {
            route: '#1C7EF7',
            routeCase: '#0B5BD0',
            halo: '#FFFFFF',
            alt: '#8FB4E8',
          };

      // 1. Alternate Routes
      const altFeatures = routes
        .map((r, i) => ({ route: r, index: i }))
        .filter((item) => item.index !== selectedRouteIndex)
        .map((item) => ({
          type: 'Feature' as const,
          properties: { index: item.index },
          geometry: item.route.geometry,
        }));

      if (!map.getSource('route-alts')) {
        map.addSource('route-alts', {
          type: 'geojson',
          data: { type: 'FeatureCollection', features: altFeatures },
        });

        map.addLayer({
          id: 'route-alts-halo',
          type: 'line',
          source: 'route-alts',
          layout: { 'line-join': 'round', 'line-cap': 'round' },
          paint: {
            'line-color': C.halo,
            'line-width': 7,
            'line-opacity': 0.7,
          },
        });

        map.addLayer({
          id: 'route-alts',
          type: 'line',
          source: 'route-alts',
          layout: { 'line-join': 'round', 'line-cap': 'round' },
          paint: {
            'line-color': C.alt,
            'line-width': 4.2,
          },
        });

        // Click to switch alternate route
        map.on('click', 'route-alts', (e) => {
          const feature = e.features?.[0];
          const idx = feature?.properties?.index;
          if (idx !== undefined && idx !== null) {
            onSelectRouteIndex(Number(idx));
          }
        });

        map.on('mouseenter', 'route-alts', () => {
          map.getCanvas().style.cursor = 'pointer';
        });
        map.on('mouseleave', 'route-alts', () => {
          map.getCanvas().style.cursor = '';
        });
      } else {
        (map.getSource('route-alts') as maplibregl.GeoJSONSource).setData({
          type: 'FeatureCollection',
          features: altFeatures,
        });
      }

      // 2. Primary Route
      const primaryGeo = {
        type: 'Feature' as const,
        geometry: activeRoute.geometry,
        properties: {},
      };

      if (!map.getSource('route-primary')) {
        map.addSource('route-primary', {
          type: 'geojson',
          data: primaryGeo,
        });

        map.addLayer({
          id: 'route-halo',
          type: 'line',
          source: 'route-primary',
          layout: { 'line-join': 'round', 'line-cap': 'round' },
          paint: {
            'line-color': C.halo,
            'line-width': 9.5,
            'line-opacity': isDark ? 0.7 : 0.95,
          },
        });

        map.addLayer({
          id: 'route-case',
          type: 'line',
          source: 'route-primary',
          layout: { 'line-join': 'round', 'line-cap': 'round' },
          paint: {
            'line-color': C.routeCase,
            'line-width': 6.8,
          },
        });

        map.addLayer({
          id: 'route-main',
          type: 'line',
          source: 'route-primary',
          layout: { 'line-join': 'round', 'line-cap': 'round' },
          paint: {
            'line-color': C.route,
            'line-width': 4.6,
          },
        });
      } else {
        (map.getSource('route-primary') as maplibregl.GeoJSONSource).setData(primaryGeo);
      }
    };

    const fitRouteBounds = () => {
      const activeRoute = routes[selectedRouteIndex];
      if (!map || !activeRoute) return;
      const coords = activeRoute.geometry.coordinates;
      if (!coords || coords.length === 0) return;

      const bounds = new maplibregl.LngLatBounds();
      coords.forEach((c) => bounds.extend(c));

      // Extend with waypoints
      waypoints.forEach((wp) => {
        bounds.extend([wp.coordinate.longitude, wp.coordinate.latitude]);
      });

      // Extend with endpoint coordinates
      endpoints.forEach((ep) => {
        if (ep.place) {
          bounds.extend([ep.place.coordinate.longitude, ep.place.coordinate.latitude]);
        }
      });

      map.resize();
      map.fitBounds(bounds, {
        padding: isDesktop
          ? { top: 70, bottom: 70, left: 440, right: 80 }
          : { top: 140, bottom: 400, left: 56, right: 120 },
        duration: 0,
      });

      const refreshBoundsAndCollisions = () => {
        updatePillCollisionsRef.current();
      };

      requestAnimationFrame(refreshBoundsAndCollisions);
      setTimeout(refreshBoundsAndCollisions, 50);
      setTimeout(refreshBoundsAndCollisions, 200);
    };

    if (map.isStyleLoaded()) {
      updateRoutes();
      fitRouteBounds();
    } else {
      const handler = () => {
        if (map.isStyleLoaded()) {
          map.off('load', handler);
          map.off('styledata', handler);
          updateRoutes();
          fitRouteBounds();
        }
      };
      map.on('load', handler);
      map.on('styledata', handler);
    }
  }, [routes, selectedRouteIndex, isDark, isDesktop, onSelectRouteIndex, waypoints, endpoints]);

  useEffect(() => {
    (window as any).__FIT_ROUTE__ = () => {
      const map = mapRef.current;
      const activeRoute = routes[selectedRouteIndex];
      if (!map || !activeRoute) return;
      const coords = activeRoute.geometry.coordinates;
      if (!coords || coords.length === 0) return;

      const bounds = new maplibregl.LngLatBounds();
      coords.forEach((c) => bounds.extend(c));

      waypoints.forEach((wp) => {
        bounds.extend([wp.coordinate.longitude, wp.coordinate.latitude]);
      });

      endpoints.forEach((ep) => {
        if (ep.place) {
          bounds.extend([ep.place.coordinate.longitude, ep.place.coordinate.latitude]);
        }
      });

      map.resize();
      map.fitBounds(bounds, {
        padding: isDesktop
          ? { top: 70, bottom: 70, left: 440, right: 80 }
          : { top: 140, bottom: 400, left: 56, right: 120 },
        duration: 0,
      });

      const refreshBoundsAndCollisions = () => {
        updatePillCollisionsRef.current();
      };

      requestAnimationFrame(refreshBoundsAndCollisions);
      setTimeout(refreshBoundsAndCollisions, 50);
      setTimeout(refreshBoundsAndCollisions, 200);
    };
  }, [routes, selectedRouteIndex, isDesktop, waypoints, endpoints]);

  const updatePillCollisions = useCallback(() => {
    const map = mapRef.current;
    if (!map || managedPillsRef.current.length === 0) return;

    const container = map.getContainer();
    const cRect = container.getBoundingClientRect();
    const W = container.clientWidth || cRect.width || 390;
    const H = container.clientHeight || cRect.height || 844;

    const chrome: Rect[] = [];
    const addChrome = (id: string) => {
      const el = document.getElementById(id);
      if (!el) return;
      const r = el.getBoundingClientRect();
      if (r.width === 0 && r.height === 0) return;
      chrome.push({
        left: r.left - cRect.left,
        top: r.top - cRect.top,
        right: r.right - cRect.left,
        bottom: r.bottom - cRect.top,
      });
    };
    addChrome('map-desktop-sidebar');
    addChrome('map-search-card');
    addChrome('map-bottom-sheet');
    addChrome('map-control-rail');
    addChrome('ticker-weather-detail');

    const items = [...managedPillsRef.current];
    const points = items.map((item) => {
      const p = map.project([item.wp.coordinate.longitude, item.wp.coordinate.latitude]);
      return { x: p.x, y: p.y };
    });

    const selectedIndex = selectedWaypointId
      ? items.findIndex((item) => item.wp.id === selectedWaypointId)
      : -1;
    const alertIndices: number[] = [];
    items.forEach((item, i) => {
      const alerts = routeAlerts.get(item.wp.id);
      if (alerts && alerts.length > 0) alertIndices.push(i);
    });

    const visible = new Set(thinTickerIndices({ points, selectedIndex, alertIndices }));
    const inChrome = (x: number, y: number) =>
      chrome.some((r) => x >= r.left && x <= r.right && y >= r.top && y <= r.bottom);

    for (let i = 0; i < items.length; i++) {
      const item = items[i]!;
      const pt = points[i]!;
      item.marker.setOffset([0, 0]);
      const offMap = pt.x < -40 || pt.x > W + 40 || pt.y < -40 || pt.y > H + 40;
      const show = visible.has(i) && !offMap && !inChrome(pt.x, pt.y);
      item.el.style.display = show ? 'flex' : 'none';
    }
  }, [selectedWaypointId, routeAlerts]);

  useEffect(() => {
    updatePillCollisionsRef.current = updatePillCollisions;
  }, [updatePillCollisions]);

  // Render Weather Pins & Endpoint Markers
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    // Clear old markers
    markersRef.current.forEach((m) => m.remove());
    markersRef.current = [];
    managedPillsRef.current = [];

    const activeRoute = routes[selectedRouteIndex];
    if (!activeRoute || waypoints.length === 0) return;

    // Keep ordinary weather pins while the drive chart is scrubbing.
    // The driver pill is a separate overlay; hiding pins here left the map blank.
    // Create every sampled waypoint; screen-space thinning only toggles display.
    waypoints.forEach((wp, idx) => {
      const weather = waypointWeather.get(wp.id);
      const isSelected = selectedWaypointId === wp.id;
      const wAlerts = routeAlerts.get(wp.id) || [];
      const hasAlert = wAlerts.length > 0;

      const isStart = idx === 0;
      const isDest = idx === waypoints.length - 1;
      const isEndpoint = isStart || isDest;

      const metric = formatTickerMetric(activeLayer, weather, unitsMode);
      const cat = getWeatherCategory(weather?.weatherCode);
      const aria = weatherLoading && metric.missing ? 'Weather loading' : metric.ariaLabel;

      const el = document.createElement('div');
      el.className = `wpin-marker wpin-${cat} ${isSelected ? 'selected' : ''}`;
      el.setAttribute('role', 'button');
      el.setAttribute('aria-label', aria);
      el.setAttribute('aria-pressed', isSelected ? 'true' : 'false');

      const valSpan = document.createElement('span');
      valSpan.className = 'wpin-val';
      valSpan.innerText = weatherLoading && metric.missing ? '…' : metric.text;
      el.appendChild(valSpan);

      const iconSpan = document.createElement('span');
      iconSpan.className = 'wpin-icon';
      if (activeLayer === 'wind') {
        iconSpan.innerHTML = getWindSvgString(16);
      } else if (activeLayer === 'precip') {
        iconSpan.innerHTML = getDropSvgString(15, '#64D2FF');
      } else if (weather) {
        iconSpan.innerHTML = getWeatherSvgString(cat, weather.isDay, 16);
      } else {
        iconSpan.innerHTML = getSunSvgString(15);
      }
      el.appendChild(iconSpan);

      // Alert badge only — severe info stays reachable via the Alerts control
      if (hasAlert) {
        const badge = document.createElement('div');
        badge.className = 'wpin-badge';
        badge.style.backgroundColor = severityColor(wAlerts[0]!.severity);
        badge.innerText = '!';
        badge.title = wAlerts[0]!.headline || 'Weather alert';
        el.appendChild(badge);
      }

      el.addEventListener('click', (e) => {
        e.stopPropagation();
        onSelectWaypointId(isSelected ? null : wp.id);
      });

      const marker = new maplibregl.Marker({ element: el, anchor: 'bottom' })
        .setLngLat([wp.coordinate.longitude, wp.coordinate.latitude])
        .addTo(map);

      markersRef.current.push(marker);
      managedPillsRef.current.push({
        marker,
        el,
        wp,
        idx,
        isEndpoint,
      });
    });

    requestAnimationFrame(() => updatePillCollisionsRef.current());
    setTimeout(() => updatePillCollisionsRef.current(), 50);
    setTimeout(() => updatePillCollisionsRef.current(), 200);
  }, [
    waypoints,
    routes,
    selectedRouteIndex,
    waypointWeather,
    routeAlerts,
    activeLayer,
    unitsMode,
    selectedWaypointId,
    onSelectWaypointId,
    weatherLoading,
    isDesktop,
    updatePillCollisions,
  ]);

  // Render Endpoint Letter Markers (A, B, C...)
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    endpointMarkersRef.current.forEach((m) => m.remove());
    endpointMarkersRef.current = [];

    const validEndpoints = (endpoints || []).filter((ep) => ep.place !== null);
    if (validEndpoints.length === 0) return;

    validEndpoints.forEach((ep, idx) => {
      const coord = ep.place!.coordinate;
      const isStart = idx === 0;
      const isDest = idx === validEndpoints.length - 1;
      const letter = String.fromCharCode(65 + idx); // A, B, C...

      let bgColor = '#007AFF'; // via accent
      if (isStart) bgColor = '#34C759'; // green A
      else if (isDest) bgColor = '#FF3B30'; // red last letter

      const el = document.createElement('div');
      el.className = `endpoint-marker route-letter-marker ${isStart ? 'start' : isDest ? 'dest' : 'via'}`;
      el.style.width = '28px';
      el.style.height = '28px';
      el.style.borderRadius = '14px';
      el.style.backgroundColor = bgColor;
      el.style.color = '#FFFFFF';
      el.style.display = 'flex';
      el.style.alignItems = 'center';
      el.style.justifyContent = 'center';
      el.style.fontWeight = '700';
      el.style.fontSize = '14px';
      el.style.fontFamily = '-apple-system, BlinkMacSystemFont, "SF Pro Text", system-ui, sans-serif';
      el.style.boxShadow = '0 2px 8px rgba(0,0,0,0.35)';
      el.style.border = '2.5px solid #FFFFFF';
      el.style.zIndex = '35';
      el.innerText = letter;

      const marker = new maplibregl.Marker({ element: el, anchor: 'center' })
        .setLngLat([coord.longitude, coord.latitude])
        .addTo(map);

      endpointMarkersRef.current.push(marker);
    });

    requestAnimationFrame(() => updatePillCollisionsRef.current());
    setTimeout(() => updatePillCollisionsRef.current(), 50);
  }, [endpoints, routes, isMapLoaded]);

  // Scrub Ticker Marker (when scrub panel is active)
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    if (scrubMarkerRef.current) {
      scrubMarkerRef.current.remove();
      scrubMarkerRef.current = null;
    }

    const activeRoute = routes[selectedRouteIndex];
    if (!activeRoute) return;

    // Match DriveChart's display fallback (0.65) so the map pill and chart cursor share an anchor
    // even before the user drags. Parent state stays null until onScrubChange fires.
    const progress = scrubProgress !== null ? scrubProgress : 0.65;
    const coords = activeRoute.geometry.coordinates;
    const pt = pointAlongProgress(coords, progress);

    // Closest waypoint weather
    const closestWp = waypoints.reduce((prev, curr) => {
      const pDiff = Math.abs(prev.routeProgress - progress);
      const cDiff = Math.abs(curr.routeProgress - progress);
      return cDiff < pDiff ? curr : prev;
    }, waypoints[0]);

    const weather = closestWp ? waypointWeather.get(closestWp.id) : undefined;
    const metric = formatTickerMetric(activeLayer, weather, unitsMode);
    const driverLabel = weatherLoading && metric.missing ? 'Weather loading' : metric.ariaLabel;
    const driverText = weatherLoading && metric.missing ? '…' : metric.text;

    const el = document.createElement('div');
    el.style.display = 'flex';
    el.style.flexDirection = 'column';
    el.style.alignItems = 'center';
    el.style.pointerEvents = 'none';
    el.style.transformOrigin = 'bottom center';

    // Ticker Box
    const ticker = document.createElement('div');
    ticker.className = 'driver-pill';
    ticker.style.padding = '4px 10px';
    ticker.style.borderRadius = '14px';
    ticker.style.boxShadow = '0 3px 8px rgba(0,0,0,0.3)';
    ticker.style.display = 'flex';
    ticker.style.alignItems = 'center';
    ticker.style.gap = '5px';
    ticker.style.whiteSpace = 'nowrap';
    ticker.setAttribute('aria-label', `Driver position ${driverLabel}`);
    ticker.innerHTML = `<span style="display:inline-flex;align-items:center;">${getCarSvgString(13, '#FFFFFF')}</span><span>${driverText}</span>`;

    // Stem from pill to the route-anchored dot
    const stem = document.createElement('div');
    stem.className = 'driver-pill-stem';
    stem.style.width = '2px';
    stem.style.height = '12px';
    stem.style.opacity = '0.95';
    stem.style.flexShrink = '0';

    // Route-anchor dot sits on the polyline (marker uses anchor: 'bottom')
    const dot = document.createElement('div');
    dot.style.width = '12px';
    dot.style.height = '12px';
    dot.style.borderRadius = '6px';
    dot.style.backgroundColor = '#FFFFFF';
    dot.style.border = '3px solid #003A8F';
    dot.style.boxShadow = '0 2px 6px rgba(0,0,0,0.35)';
    dot.style.flexShrink = '0';
    dot.style.marginTop = '-1px';

    el.appendChild(ticker);
    el.appendChild(stem);
    el.appendChild(dot);

    const marker = new maplibregl.Marker({ element: el, anchor: 'bottom' })
      .setLngLat([pt.longitude, pt.latitude])
      .addTo(map);

    scrubMarkerRef.current = marker;
  }, [scrubProgress, routes, selectedRouteIndex, waypoints, waypointWeather, unitsMode, activeLayer, weatherLoading]);

  return (
    <div style={{ position: 'absolute', inset: 0, overflow: 'hidden' }}>
      <div ref={mapContainerRef} style={{ width: '100%', height: '100%' }} />

      {/* Map legal attribution overlay */}
      <div
        style={{
          position: 'absolute',
          bottom: 12,
          right: 14,
          fontSize: 10,
          fontWeight: 500,
          color: 'var(--label2)',
          textShadow: isDark ? '0 0 4px #000' : '0 0 4px #FFF',
          pointerEvents: 'none',
          zIndex: 10,
        }}
      >
        © OpenStreetMap, Open-Meteo, OSRM, Photon
      </div>
    </div>
  );
};
